import ExpandMore from '@mui/icons-material/ExpandMore'
import Send from '@mui/icons-material/Send'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useState } from 'react'
import FindingCommentCard from './FindingCommentCard'
import { findDCSByMetricEventId } from '../../utils/dataCollection/findDCSByMetricEventId'
import { useFindingComments, useSaveComment } from '../../hooks/data/useFindingComments'
import { isReportedByQueryLayer } from '../../utils/Errors/isReportedByQueryLayer'
import { useCustomSnackbarError } from '../../utils/Snackbar/useCustomSnackbarError'
import AppContext from '../../context/appContext'

const FindingCommentsAccordion = ({ finding }) => {
  const { userRoleSelected, currentFarm } = useContext(AppContext)
  const { handleError } = useCustomSnackbarError()

  const [expanded, setExpanded] = useState(false)
  const [actualComment, setActualComment] = useState('')
  // The DCS lookup is a network round-trip that precedes the mutation, so mutation.isPending
  // alone would leave the send button live during it and allow the same comment to be posted twice.
  const [isSending, setIsSending] = useState(false)

  const { data: comments = [] } = useFindingComments(finding?.id)
  const saveCommentMutation = useSaveComment()

  const sendComment = async () => {
    if (!actualComment || isSending) return
    setIsSending(true)
    try {
      let dataCollectionStatement
      try {
        dataCollectionStatement = await findDCSByMetricEventId({
          farmId: currentFarm.id,
          userRole: userRoleSelected,
          metricEventId: finding.metricEventId,
        })
      } catch (error) {
        if (!isReportedByQueryLayer(error)) handleError(error, 'Error al guardar el comentario')
        return
      }
      if (!dataCollectionStatement) {
        handleError(
          new Error('No se encontró la declaración de recolección de datos para este comentario'),
          'Error al guardar el comentario',
        )
        return
      }

      await saveCommentMutation.mutateAsync({
        dataCollectionStatementId: dataCollectionStatement.id,
        findingId: finding.id,
        userRole: userRoleSelected,
        message: actualComment,
      })
      setActualComment('')
    } catch {
      // The mutation's failure already reached the user through the global error bridge.
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Stack direction="column" pb={0.5} width="100%">
      <Accordion
        expanded={expanded}
        sx={{
          width: '100%',
          boxShadow: 'none',
        }}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            margin: 0,
            minHeight: '30px',
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-root': {
              minHeight: '30px',
              '&.Mui-expanded': {
                minHeight: '30px',
              },
            },
          }}
        >
          <Typography sx={{ width: '50%', textAlign: 'start', fontSize: 14 }}>
            Comentarios
            <Badge
              badgeContent={comments.length}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: 'blue',
                  color: 'white',
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                  minWidth: '18px',
                  minHeight: '16px',
                  padding: '0 6px',
                  right: '-20px',
                },
              }}
            />
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {comments.length > 0 ? (
            <Paper
              elevation={0}
              sx={{
                padding: 1,
                margin: 0.5,
                borderRadius: '5px 5px 5px 5px',
                border: '1px solid #dcdcdc',
                backgroundColor: 'inherit',
              }}
            >
              {comments.map(comment => {
                return <FindingCommentCard key={comment.id} comment={comment} />
              })}
            </Paper>
          ) : null}
          <Grid container m={0.5}>
            <Grid item xs={11}>
              <TextField
                fullWidth
                multiline
                disabled={isSending}
                rows={2}
                value={actualComment}
                onChange={e => setActualComment(e.target.value)}
              />
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              xs={1}
            >
              <Button
                aria-label="Enviar comentario"
                disabled={isSending}
                endIcon={
                  isSending ? (
                    <CircularProgress
                      size={20}
                      sx={{
                        color: 'white',
                      }}
                    />
                  ) : (
                    <Send />
                  )
                }
                sx={{
                  minWidth: 0,
                  maxWidth: 0,
                  '& .MuiButton-endIcon': {
                    margin: 0,
                  },
                }}
                variant="contained"
                onClick={sendComment}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  )
}

export default FindingCommentsAccordion
